/**
 * Default debounce interval delay.
 * @type {Number}
 */
export const DEBOUNCE_DEFAULT = 500;

/**
 * Base debounce implementation. Creates a wrapping function that executes @param callback after elapsed @param delay.
 *
 * @param {Function} callback function of interest to be executed. Returned value not preserved.
 * @param {Number} delay millisecond interval delaying execution of function of interest.
 * @returns {Function} wrapping function that, when invoked, executes the function of interest after interval delay.
 */
export const debounce = (callback, delay = DEBOUNCE_DEFAULT) => {
	let timeoutID;

	// ...args does not yet exist, defined when the returned anonymous function is invoked
	return (...args) => {
		clearTimeout(timeoutID);
		timeoutID = setTimeout(() => {
			callback(...args);
		}, delay);
	};
};

/**
 * Asynchronous debounce implementation. Creates a wrapping function that executes asynchronous @param callback after elapsed @param delay.
 *
 * @param {Function} callback asynchronous function of interest to be executed. Return value preserved.
 * @param {Number} delay millisecond interval delaying execution of function of interest.
 * @returns {Function} wrapping function that, when invoked, executes the asynchronous function of interest after interval delay.
 */
export const debounceAsync = (callback, delay = DEBOUNCE_DEFAULT) => {
	let timeoutID;

	return (...args) => {
		return new Promise(resolve => {
			clearTimeout(timeoutID);
			timeoutID = setTimeout(async () => {
				resolve(callback(...args));
			}, delay);
		});
	};
};
